import React from 'react';
import { motion } from 'framer-motion';
import { FiPhoneMissed, FiClock, FiDollarSign, FiUsers, FiTrendingUp } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Problem = () => {
  const painPoints = [
    {
      icon: FiPhoneMissed,
      title: '38% of support calls go unanswered after hours',
      desc: 'Your customers call at 6pm, on weekends, on holidays. Nobody picks up. They call your competitor instead.',
    },
    {
      icon: FiClock,
      title: 'Average hold time kills customer satisfaction',
      desc: 'Every minute on hold increases abandonment by 5%. Your team is stretched thin and callers are hanging up.',
    },
    {
      icon: FiDollarSign,
      title: 'Hiring support staff costs $3,500–5,000/month per person',
      desc: 'Salary, benefits, training, turnover. And they still only work 8 hours a day, 5 days a week.',
    },
    {
      icon: FiUsers,
      title: 'Tier-1 calls waste your best people\'s time',
      desc: '60–70% of inbound calls are routine — status inquiries, product questions, troubleshooting walkthroughs. Your senior team shouldn\'t be fielding these.',
    },
    {
      icon: FiTrendingUp,
      title: 'Scaling call capacity means scaling headcount',
      desc: 'Every spike in call volume means overtime, hiring, or missed calls. AI scales instantly with no ramp-up time.',
    },
  ];

  return (
    <section className="py-12 md:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="text-center mb-10 md:mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Your Customers Are Waiting on Hold.<br />Your Team Can't Keep Up.
          </h2>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto">
            High call volume, limited staff, and 24/7 customer expectations are a recipe for churn.
          </p>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12">
          {painPoints.map((point, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-slate-50 p-6 rounded-2xl border border-slate-200 hover:border-slate-300 transition-colors"
            >
              <div className="w-12 h-12 bg-slate-200 rounded-xl flex items-center justify-center mb-4">
                <SafeIcon icon={point.icon} className="w-6 h-6 text-slate-700" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">{point.title}</h3>
              <p className="text-slate-600 text-sm">{point.desc}</p>
            </motion.div>
          ))}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="bg-slate-900 p-6 rounded-2xl flex flex-col justify-center items-center text-center text-white relative overflow-hidden"
          >
            <div className="relative z-10">
              <h3 className="text-2xl font-bold mb-3">The Result</h3>
              <p className="text-slate-300 text-lg">
                You're spending <span className="text-blue-400 font-bold">$50K–100K+/year</span> on calls that could be handled automatically.
              </p>
            </div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/20 blur-3xl rounded-full pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-slate-500/20 blur-3xl rounded-full pointer-events-none"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Problem;